import Service from '@ember/service';
import { later } from '@ember/runloop';
import Ember from 'ember';
import { inject as service } from '@ember/service';
import ENV from 'cio/config/environment';
import SnackbarService, { Snackbar } from 'cio/services/snackbar';

export enum AlertState {
  Success = 'success',
  Warning = 'warning',
  Error = 'error',
}

type Alert = {
  id: string;
  state: AlertState;
  message: string;
  title?: string;
  action?: {
    title: string;
    onClick?: () => unknown;
    link?: {
      route: string;
      query?: Record<string, any>;
    };
  };
};

export default class AlertsService extends Service {
  @service snackbar!: SnackbarService;

  current: Alert[] = [];

  sticky(id: Alert['id'], message: Alert['message'], title?: Alert['title'], action?: Alert['action']) {
    this._add(id, AlertState.Warning, message, title, 0, action);
  }

  success(
    id: Snackbar['id'],
    message: Snackbar['message'],
    title?: string,
    _duration?: number,
    action?: Snackbar['action'],
  ) {
    let snackbarMessage = message;

    if (title != null) {
      snackbarMessage = `<strong>${title}</strong> ${message}`;
    }

    this.remove(id);
    this.snackbar.add(id, snackbarMessage, action);
  }

  warning(
    id: Alert['id'],
    message: Alert['message'],
    title?: Alert['title'],
    duration?: number,
    action?: Alert['action'],
  ) {
    this._add(id, AlertState.Warning, message, title, duration, action);
  }

  error(
    id: Alert['id'],
    message: Alert['message'],
    title?: Alert['title'],
    duration?: number,
    action?: Alert['action'],
  ) {
    this._add(id, AlertState.Error, message, title, duration, action);
  }

  remove(id: Alert['id']) {
    let alert = this.current.findBy('id', id);
    if (alert) {
      this.current.removeObject(alert);
    }
  }

  clearAlerts() {
    this.set('current', []);
  }

  _add(
    id: Alert['id'],
    state: AlertState,
    message: Alert['message'],
    title?: Alert['title'],
    duration?: number,
    action?: Alert['action'],
  ) {
    if (duration == null) {
      duration = ENV.APP.alertDuration;
    }

    let alert = { id, state, message, title, action };

    this.remove(id);
    this.current.pushObject(alert);

    if (duration > 0 && !Ember.testing) {
      later(() => this.current.removeObject(alert), duration);
    }
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    alerts: AlertsService;
  }
}
